import React, { useState } from 'react'
import { Col, Row } from 'reactstrap'
import { faPlay, faSave, faTrash } from '@fortawesome/free-solid-svg-icons'
import Card from 'src/components/Card'
import { useReportBuilderContext } from '../ReportBuilderContext'
import { useMutateCreateReportBuilderReport } from 'src/queries/cat/reporting/reportbuilder/useMutateCreateReportBuilderReport'
import { useMutateReportBuilderReportById } from 'src/queries/cat/reporting/reportbuilder/useMutateReportBuilderReportById'
import { useMutateDeleteReportBuilderReport } from 'src/queries/cat/reporting/reportbuilder/useMutateDeleteReportBuilderReport'
import { reportBuilderReportByIdDocument } from 'src/queries/cat/reporting/reportbuilder/useReportBuilderReportById'
import { reportBuilderReportsDocument } from 'src/queries/cat/reporting/reportbuilder/useReportBuilderReports'
import { useInvalidateQueryByDocument, useQueryPrefetchByDocument } from 'src/hooks/useGraphQL'
import { useNavigate } from 'react-router-dom'
import { EnumReportBuilderReportModel } from 'src/gql/graphql'
import ModelSelector from './ModelSelector'
import QueryBuilder from './QueryBuilder'
import FieldSelector from './FieldSelector'
import SaveModal from './SaveModal'
import DeleteModal from './DeleteModal'
import './style.css'

interface ModalState {
  mode: 'create' | 'copy' | 'update'
  initialTitle?: string
}

const QueryBuilderCard: React.FC = () => {
  const { query, runQuery, isNew, id, selectedFields, model, title } = useReportBuilderContext()
  const [modal, setModal] = useState<ModalState | undefined>(undefined)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const createMutation = useMutateCreateReportBuilderReport()
  const updateMutation = useMutateReportBuilderReportById()
  const deleteMutation = useMutateDeleteReportBuilderReport()
  const invalidateReport = useInvalidateQueryByDocument(reportBuilderReportByIdDocument)
  const prefetchReports = useQueryPrefetchByDocument(reportBuilderReportsDocument)
  const navigate = useNavigate()

  const handleSave = (): void => {
    if (isNew) {
      setModal({ mode: 'create' })
    } else {
      setModal({ mode: 'update', initialTitle: title ?? '' })
    }
  }

  const handleSaveAs = (): void => {
    setModal({ mode: 'copy' })
  }

  const handleDelete = async (): Promise<void> => {
    await deleteMutation.mutateAsync({ id })
    void prefetchReports()
    navigate('/cat/views/reportbuilder')
    setDeleteOpen(false)
  }

  const handleModalSave = async (newTitle: string): Promise<void> => {
    if (modal?.mode === 'update') {
      await updateMutation.mutateAsync(
        { id, record: { title: newTitle, model: model as EnumReportBuilderReportModel, query, fields: selectedFields.map(f => f.name) } }
      )
      void invalidateReport({ id })
      void prefetchReports()
    } else {
      const result = await createMutation.mutateAsync({
        record: { title: newTitle, model: model as EnumReportBuilderReportModel, query, fields: selectedFields.map(f => f.name) }
      })
      const newId = result.cat?.reporting?.builder?.report?.createOne?.recordId
      if (typeof newId === 'string') {
        void prefetchReports()
        navigate(`/cat/views/reportbuilder/${newId}`)
      }
    }
    setModal(undefined)
  }

  const saveDisabled = query === undefined

  return (
    <>
      <Card
        title='Query Builder'
        primaryAction={[
          {
            key: 'model-selector',
            render: () => <ModelSelector />
          },
          { key: 'run', icon: faPlay, text: 'Run Query', onClick: runQuery }
        ]}
        secondaryActions={[
          { key: 'save', icon: faSave, text: 'Save', onClick: handleSave, disabled: saveDisabled },
          ...(!isNew
            ? [
                { key: 'save-as', icon: faSave, text: 'Save As', onClick: handleSaveAs },
                { key: 'delete', icon: faTrash, iconProps: { className: 'text-danger' }, text: 'Delete', onClick: () => setDeleteOpen(true) }
              ]
            : [])
        ]}
      >
        <Row>
          <Col>
            <QueryBuilder />
          </Col>
          <Col xs='auto' className='border-start p-3'>
            <FieldSelector />
          </Col>
        </Row>
      </Card>
      <SaveModal
        isOpen={modal !== undefined}
        toggle={() => setModal(undefined)}
        onSave={handleModalSave}
        initialTitle={modal?.initialTitle}
        mode={modal?.mode ?? 'create'}
      />
      <DeleteModal
        isOpen={deleteOpen}
        toggle={() => setDeleteOpen(false)}
        onDelete={handleDelete}
        isLoading={deleteMutation.isLoading}
      />
    </>
  )
}

export default QueryBuilderCard
